/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-rest-params */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  EvaluationScoreListOptionsInterface,
  TaskEduContentInterface,
  TaskInterface,
  TaskLevelSettingInterface,
} from '../../+models';
import { TaskDataResponseInterface } from '../../data';
import {
  CreateAdaptiveTaskResponseInterface,
  CreateEvaluationAndAdaptiveTaskResponseInterface,
  CreateEvaluationTaskConfigInterface,
  CreateEvaluationTaskResponseInterface,
  DeleteEvaluationTasksResultInterface,
  StartEvaluationTaskResultInterface,
  StopEvaluationTaskResultInterface,
  TaskAssigneeIdsInterface,
  UpdateEvaluationScoreListSubjectResponseInterface,
  UpdateTaskResultInterface,
} from '../../tasks';
import { ModelApiBase, PolpoApi } from '../shared';

@Injectable({ providedIn: 'root' })
export class TaskApi extends ModelApiBase {
  constructor(api: PolpoApi) {
    super(api, 'Tasks');
  }

  getData(taskId: number, fields?: string, since?: string | number): Observable<TaskDataResponseInterface> {
    const path = `${taskId}/data`;
    const params = { fields, since };
    const httpParams = this._getHttpParams(params);

    return this.get(path, httpParams);
  }

  setTaskLevelSettings(
    taskId: number,
    taskLevelSettings: TaskLevelSettingInterface[]
  ): Observable<TaskLevelSettingInterface[]> {
    const path = `${taskId}/set-task-level-settings`;
    return this.post(path, undefined, taskLevelSettings);
  }

  linkEduContents(taskId: number, eduContentId: number): Observable<TaskEduContentInterface> {
    const path = `${taskId}/eduContents/rel/${eduContentId}`;
    return this.put<TaskEduContentInterface>(path);
  }

  updateTasks(update: {
    partialTasks: Partial<TaskInterface>[];
    studentIds: number[];
  }): Observable<UpdateTaskResultInterface> {
    const path = 'update-tasks';
    return this.patch(path, undefined, update);
  }

  duplicateTask(taskId: number) {
    const path = `${taskId}/duplicate`;
    return this.post<{ errors: []; success: { task: TaskInterface } }>(path, undefined);
  }

  createForEvaluation(
    task: TaskInterface,
    evaluationSubjectIds: number[],
    evaluationScoreListOptions?: EvaluationScoreListOptionsInterface
  ): Observable<CreateEvaluationTaskResponseInterface> {
    const path = 'create-for-evaluation';
    const body = { task, evaluationSubjectIds, ...(evaluationScoreListOptions ? { evaluationScoreListOptions } : {}) };

    return this.post(path, undefined, body);
  }

  createEvaluationAndAdaptiveTask(
    task: TaskInterface,
    assignees: TaskAssigneeIdsInterface,
    evaluationConfig: CreateEvaluationTaskConfigInterface
  ): Observable<CreateEvaluationAndAdaptiveTaskResponseInterface> {
    const path = 'create-evaluation-and-adaptive-task';
    const body = { task, assignees, evaluationConfig };

    return this.post(path, undefined, body);
  }

  createEvaluationTask(
    task: TaskInterface,
    assignees: TaskAssigneeIdsInterface,
    evaluationConfig: CreateEvaluationTaskConfigInterface
  ): Observable<CreateEvaluationTaskResponseInterface> {
    const path = 'create-evaluation-task';
    const body = { task, assignees, evaluationConfig };

    return this.post(path, undefined, body);
  }

  destroyEvaluationTasks(taskIds: number[]): Observable<DeleteEvaluationTasksResultInterface> {
    const path = 'destroy-evaluation-tasks';
    return this.post(path, undefined, taskIds);
  }

  destroyTasks(taskIds: number[]): Observable<UpdateTaskResultInterface> {
    const path = 'destroy-tasks';
    return this.post(path, undefined, taskIds);
  }

  //Used in the new evaluations (/evaluation-tasks)
  startEvaluationTask(taskId: number, studentIds?: number[]): Observable<StartEvaluationTaskResultInterface> {
    const path = `${taskId}/start-evaluation-task`;
    const body = studentIds ? { studentIds } : undefined;
    return this.post(path, undefined, body);
  }

  //Used in the new evaluations (/evaluation-tasks)
  stopEvaluationTask(taskId: number): Observable<StopEvaluationTaskResultInterface> {
    const path = `${taskId}/stop-evaluation-task`;
    return this.post(path, undefined, undefined);
  }

  updateEvaluationScoreListSubjects(
    taskId: number,
    evaluationSubjectIds: number[]
  ): Observable<UpdateEvaluationScoreListSubjectResponseInterface> {
    const path = `${taskId}/update-evaluation-subjects`;
    return this.post(path, undefined, { evaluationSubjectIds });
  }

  createAdaptiveTaskFromEvaluationTask(evaluationTaskId: number): Observable<CreateAdaptiveTaskResponseInterface> {
    const path = `${evaluationTaskId}/create-adaptive-task`;

    return this.post(path);
  }
}
